import {Injectable} from '@angular/core';
import {ConnectorService} from './connector.service';
import {Observable} from 'rxjs';
import {CatalogItemModel, CatalogModel, PropertyModel, ValuePropertyModel} from '../../models/property.model';

@Injectable({
    providedIn: 'root'
})
export class PropertiesService extends ConnectorService {

    /**
     * Получение кастомных полей
     * @param type = 'board' | 'doc' | 'contract'
     */
    getProperties(type: 'board' | 'doc' | 'contract'): Observable<{ properties: PropertyModel[] }> {
        return this.get('instance/settings/properties/' + type);
    }

    /**
     * Создание кастомного поля
     * @param data {
     *         type: 'board' | 'doc' | 'contract',
     *         title: string,
     *         property_type: 'property_file' | 'property_member' | 'property_text' | 'property_boolean' | 'property_catalog',
     *         property_type_target?: для property_type === 'property_member' - 'all' | 'outer' | 'inner'
     *                                для property_type === 'property_catalog' - id справочника
     *     }
     */
    createProperty(data: {
        type: 'board' | 'doc' | 'contract',
        title: string,
        property_type: string,
        property_type_target?: string,
        is_multiform: boolean,
    }): Observable<any> {
        return this.post('instance/settings/properties/', data);
    }

    /**
     * Получение информации поля
     * @param propType
     * @param propertyId
     */
    getPropertyInfoById(propType: 'board' | 'doc', propertyId: number): Observable<{ relations: any }> {
        return this.get('instance/settings/properties/' + propType + '/' + propertyId);
    }

    /**
     * Изменение поля
     * @param data
     */
    updateProperty(data: {
        id: number,
        type: 'board' | 'doc' | 'contract',
        title: string,
        property_type_target?: string
    }): Observable<any> {
        return this.put('instance/settings/properties/' + data.id, data);
    }

    /**
     * Удаление кастомного поля
     * @param propertyId
     * @param type
     */
    deleteProperty(propertyId: number, type: 'board' | 'doc' | 'contract'): Observable<{ message: string }> {
        return this.delete('instance/settings/properties/' + propertyId, {type});
    }

    /**
     * Сохранение\удаление подключенных проперти в доске, отправляется весь стейт проперти,
     * для удаления необходимо исключить из стейта необходимый проперти
     * @param boardAlias
     * @param data {
     *             property_default_view: boolean - отображение проперти в задаче
     *             property_id: number - id проперти
     *         } - массив проперти
     */
    addOrDeletePropertyInBoard(
        boardAlias: string,
        data: {
            property_default_view: boolean,
            property_id: number
        }[]
    ): Observable<{ properties: ValuePropertyModel[] }> {
        return this.put('instance/crm/boards/properties/' + boardAlias, data, false);
    }

    /**
     * Получение подключенных проперти к документам
     * @param targetType - 'file' | 'collection' | 'folder'
     */
    getConnectedDocProperty(targetType: 'file' | 'collection' | 'folder'): Observable<{
        properties: ValuePropertyModel[]
    }> {
        return this.get('instance/edi/properties/' + targetType);
    }

    /**
     * Сохранение\удаление подключенных проперти в папках, коллекциях и файлах, отправляется весь стейт проперти,
     * для удаления необходимо исключить из стейта необходимый проперти
     * @param target
     * @param data {
     *             property_default_view: boolean - отображение проперти в задаче
     *             property_id: number - id проперти
     *         } - массив проперти
     */
    addOrDeletePropertyInDoc(
        target: 'file' | 'collection' | 'folder',
        data: {
            property_default_view: boolean,
            property_id: number
        }[]
    ): Observable<{ properties: ValuePropertyModel[] }> {
        return this.put('instance/edi/properties/' + target, data, false);
    }

    /**
     * Создание нового каталога
     * @param data
     * {
     *     title: string,
     *     description: string,
     *     is_open: boolean - false - в каталог может вносить изменения только администратор
     * }
     */
    createNewCatalog(data: { title: string, description: string, is_open: boolean }): Observable<{
        catalog: CatalogModel,
        message: string,
    }> {
        return this.post('instance/settings/catalog', data);
    }

    /**
     * Получение всех каталогов
     */
    getAllCatalogs(): Observable<{ catalog: CatalogModel[] }> {
        return this.get('instance/settings/catalog/all');
    }

    /**
     * Удаление каталога
     * @param catalogId
     * @param force
     */
    deleteCatalog(catalogId: number, force: boolean): Observable<{ message: string }> {
        return this.delete('instance/settings/catalog/' + catalogId, {force});
    }

    /**
     * Обновление каталога
     * @param catalogId
     * @param data
     * {
     *      title: string,
     *      description: string,
     *      is_open: boolean - false - в каталог может вносить изменения только администратор
     *  }
     */
    editCatalog(catalogId: number, data: { title: string, description: string, is_open: boolean }): Observable<{
        catalog: CatalogModel
    }> {
        return this.put('instance/settings/catalog/' + catalogId, data);
    }

    /**
     * Удаление item'а из каталога
     * @param itemId
     */
    deleteCatalogItem(itemId: number): Observable<{ message: string }> {
        return this.delete('instance/settings/item/' + itemId);
    }

    /**
     * Создать элемент каталога
     * @param data { catalog_id: number, items_text: string }
     */
    createCatalogItem(data: { catalog_id: number, items_text: string }): Observable<{
        catalog_item: CatalogItemModel,
        message: string,
    }> {
        return this.post('instance/settings/item', data);
    }

    /**
     * Редактировать элемент каталога
     * @param itemId
     * @param data { items_text: string }
     */
    editCatalogItem(itemId: number, data: string): Observable<{ catalog: CatalogModel }> {
        return this.put('instance/settings/item/' + itemId, {items_text: data});
    }

    /**
     * Получение каталога по его id
     * @param catalogId
     */
    getCatalogById(catalogId: number): Observable<{ catalog: CatalogModel }> {
        return this.get('instance/settings/catalog/' + catalogId);
    }
}
